import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import axios from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  ICandidateStageStatusPipelineSetting,
  ICandidate,
  IUserDTO,
  updateCandidateSubmission,
} from 'wisestep-api';
import {
  WsButton,
  WsDateTimePicker,
  WsTypography,
  WsSelect,
  WsSelectObj,
  WsDrawer,
  formattedDateISO,
  getDateUsingFormat,
  getStandardTime,
  getCurrentZoneAbbr,
  Item,
} from 'wisestep-base';

import { useUserContext } from '../../context';
import ActionFooter from '../../features/conversationWidget/components/ActionFooter';
import { useStageStatusContext } from '../../features/submissions/context/StageStatusContext';
import useCandidateStatusesByStage from '../../hooks/useCandidateStatusesByStage';
import { getCandidateStagesInWsSelectObj } from '../../utils/CandidateStageStatusHelper';
import { failed, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

import jobCandidateInteraction from './payload';

export interface ICandidateStatuses {
  status: string;
  value: string;
}
export type BulkUpdateStatusDrawerPropsType = {
  candidates: ICandidate[];
  openStatusModal: boolean;
  onClose: () => void;
  refreshAll: () => void;
  onUpdateAllClick: () => void;
  updateUI?: (candidate: ICandidate[]) => void;
};
interface IUpdateStatusModal {
  stage: string;
  status: string;
  stageDate: Date;
}

const BulkUpdateStatusDrawer = ({
  candidates,
  openStatusModal,
  onClose,
  refreshAll,
  updateUI,
  onUpdateAllClick,
}: BulkUpdateStatusDrawerPropsType) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const stageStatus = useStageStatusContext();

  const defaultValues: IUpdateStatusModal = {
    stage: '',
    status: '',
    stageDate: new Date(),
  };
  const methods = useForm({
    defaultValues: defaultValues,
  });

  const { handleSubmit, watch } = methods;
  const selectedStage = watch('stage');
  const selectedStatus = watch('status');

  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const [candidateStageStatusSettings, setCandidateStageStatusSettings] =
    useState<ICandidateStageStatusPipelineSetting>();

  const [stages, setStages] = useState<WsSelectObj[]>([]);

  const statuses = useCandidateStatusesByStage(
    candidateStageStatusSettings?.stageStatusPipeline ?? '',
    selectedStage
  );

  const statusesObjects: WsSelectObj[] = statuses?.map((status) => ({
    label: `${status.value}`,
    value: `${status.value}`,
  }));

  const [candidatesList, setcandidatesList] = useState<ICandidate[]>([]);

  useEffect(() => {
    setCandidateStageStatusSettings(stageStatus);
    setStages(getCandidateStagesInWsSelectObj(stageStatus));
    setcandidatesList(candidates);
  }, [stageStatus, candidates]);

  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const loggedInUserName = loggedInUser.firstName ?? loggedInUser.lastName ?? loggedInUser.email;

  const onSubmit = async (data: FieldValues) => {
    if (!data.stage) {
      setNotification(failed('Please Select Stage'));
      return;
    }
    if (!data.status) {
      setNotification(failed('Please Select Status to Update'));
      return;
    }
    setDisableSubmit(true);
    const arr: ICandidate[] = [];
    candidatesList.forEach((candidate) => {
      const obj = { ...jobCandidateInteraction };
      obj.id = candidate.id ?? '';
      obj.applicationStage = data.stage;
      obj.applicationState = data.status;
      obj.stageDate = data.stageDate;

      let editedNote = '';

      const endingNote = ` with Stage Date ${getDateUsingFormat(
        data.stageDate,
        'DD MMMM, YYYY, h:mm a'
      )} ${getStandardTime()} - UTC ${getCurrentZoneAbbr()}`;

      if (
        (!isEmpty(candidate.applicationStage) && selectedStage !== candidate.applicationStage) ||
        (!isEmpty(candidate.applicationState) && selectedStatus !== candidate.applicationState)
      ) {
        editedNote = `The Candidate Status has now been updated from <strong> 
                      ${candidate.applicationStage}  </strong> and <strong> 
                      ${candidate.applicationState}  </strong> to <strong> 
                      ${selectedStage}  </strong> and <strong> 
                      ${selectedStatus}  </strong>`;
      } else {
        editedNote = `The Candidate Status is now  <strong> 
                      ${selectedStage}  </strong> and <strong> 
                      ${selectedStatus}`;
      }

      editedNote = `${editedNote}. ${!isEmpty(endingNote) ? endingNote : ''}`;

      obj.candidateNotes = [
        {
          notes: editedNote,
          createdBy: loggedInUserName ?? '',
          createdAt: formattedDateISO(new Date()),
        },
      ];

      obj.stageAndStateActions = [
        {
          stage: data.stage,
          state: data.status,
          id: '',
          updatedBy: loggedInUserName ?? '',
          updatedOn: formattedDateISO(new Date()),
        },
      ];
      arr.push(obj);
    });
    try {
      const response = await updateCandidateSubmission(arr);
      setNotification(success('Submission Status Updated Successfully'));
      onUpdateAllClick();
      refreshAll();
      updateUI && updateUI(response.data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const response = e.response;
        if (response?.status === 500) {
          setNotification(failed('Unable to update submissions.'));
        } else if (e.code === 'ECONNABORTED') {
          setNotification(failed('The update is taking too long, please try again later.'));
        } else {
          setNotification(failed('An error occurred. Please try again later.'));
        }
      } else {
        setNotification(failed('An error occurred. Please try again later.'));
      }
      setDisableSubmit(false);
    }
  };

  return (
    <WsDrawer
      onClose={onClose}
      title={'Bulk Update of Candidates Stage and Status'}
      open={openStatusModal}
      size="large"
      iconType="arrow"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container sx={{ p: 3 }}>
            <Grid
              container
              spacing={3}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
              }}
            >
              <Grid item xs={2} sx={{ mb: -1 }}>
                <WsTypography bold>Update the candidates to </WsTypography>
              </Grid>
              <Grid item xs={3}>
                <WsSelect label="Stage" name="stage" selectArray={stages} />
              </Grid>
              <Grid item xs={3}>
                <WsSelect label="Status" name="status" selectArray={statusesObjects} />
              </Grid>
              <Grid item xs={3}>
                <label htmlFor="stageDate">Stage Date</label>
                <WsDateTimePicker label="" name="stageDate" size="small" disablePast={false} />
              </Grid>
              <Grid item xs></Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ m: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Job</TableCell>
                    <TableCell>Current Stage</TableCell>
                    <TableCell>Current Status</TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidatesList.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.mappedJobTitle}</TableCell>
                      <TableCell>{row.applicationStage}</TableCell>
                      <TableCell>{row.applicationState}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() =>
                            setcandidatesList(candidatesList.filter((i) => i.id != row.id))
                          }
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Item sx={{ mb: 6 }}></Item>
            <ActionFooter>
              <Grid item xs={8} display="flex" justifyContent="end">
                <WsButton
                  variant="contained"
                  type="submit"
                  disabled={disableSubmit || candidatesList.length == 0}
                >
                  Update All
                </WsButton>
              </Grid>
            </ActionFooter>
          </Grid>
        </form>
      </FormProvider>
    </WsDrawer>
  );
};

export default BulkUpdateStatusDrawer;
